import { useContext, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "mantine-react-table/styles.css";
import "@mantine/charts/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/carousel/styles.css";
import "@mantine/dropzone/styles.css";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import {
  MantineProvider,
  Container,
  Title,
  Text,
  Button,
  Stack,
  Center,
  Box,
} from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { IconHome, IconLock, IconAlertTriangle } from "@tabler/icons-react";
import { Provider as RollbarProvider, ErrorBoundary } from "@rollbar/react";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { rollbarConfig, hasRollbarToken } from "./utils/rollbar";
import ErrorFallback from "./components/ErrorFallback";
import LoadingLayout from "./layouts/LoadingLayout";
import Layout from "./layouts/Layout";
import { AuthProvider, AuthContext } from "./contexts/AuthContext";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const InspectionsPage = lazy(() => import("./pages/InspectionsPage"));
const InspectionPage = lazy(() => import("./pages/InspectionPage"));
const AssetsPage = lazy(() => import("./pages/AssetsPage"));
const AssetPage = lazy(() => import("./pages/AssetPage"));
const OrganisationsPage = lazy(() => import("./pages/OrganisationsPage"));
const OrganisationPage = lazy(() => import("./pages/OrganisationPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const UploadsPage = lazy(() => import("./pages/UploadsPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ActivationPage = lazy(() => import("./pages/ActivationPage"));
const AuthCallbackPage = lazy(() => import("./pages/AuthCallbackPage"));
const AccountSettingsPage = lazy(() => import("./pages/AccountSettingsPage"));

import { InspectionsProvider } from "./contexts/InspectionsContext";
import { FoldersProvider } from "./contexts/FoldersContext";
import { FiltersProvider } from "./contexts/FiltersContext";
import { AssetsProvider } from "./contexts/AssetsContext";
import { UploadsProvider } from "./contexts/UploadsContext";
import { UploadProvider } from "./contexts/UploadContext";
import { ExportsProvider } from "./contexts/ExportsContext";
import { OrganisationsProvider } from "./contexts/OrganisationsContext";
import { OrganisationProvider } from "./contexts/OrganisationContext";
import { UsersProvider } from "./contexts/UsersContext";
import { UserProvider } from "./contexts/UserContext";
import { InspectionProvider } from "./contexts/InspectionContext";

import { OrgTypes, UserLevels } from "./utils/constants";
import { NetworkStatusNotification } from "./components/NetworkStatusNotification";
import ProductFruitsWrapper from "./components/ProductFruitsWrapper";

function HomeRedirect() {
  const { isAuthenticated, authInitializing, user } = useContext(AuthContext);
  if (authInitializing) return <LoadingLayout />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.isStaff) {
    return <Navigate to="/organisations" replace />;
  } else if (user?.userLevel === UserLevels.UPLOAD_ONLY) {
    return <Navigate to="/uploads" replace />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
}

const PublicRoute = ({ children }) => {
  const { isAuthenticated, authInitializing } = useContext(AuthContext);
  if (authInitializing) return <LoadingLayout />;
  return !isAuthenticated ? children : <HomeRedirect />;
};

const ProtectedRoute = ({
  children,
  requireAuth = true,
  requireOrgType,
  requireUserLevel,
  requireStaff = false,
  allowUploadOnly = false,
  allowStaff = false,
  requireWorkingAsAssetOwner = false,
}) => {
  const {
    isAuthenticated,
    authInitializing,
    organisation,
    user,
    appInitializing,
    workingInIsAssetOwner,
  } = useContext(AuthContext);
  if (authInitializing || appInitializing) return <LoadingLayout />;

  if (requireAuth && !isAuthenticated) return <Navigate to="/login" replace />;

  if (user?.userLevel === UserLevels.UPLOAD_ONLY && !allowUploadOnly) {
    return <ForbiddenPage />;
  }

  if (requireOrgType && requireOrgType !== "ANY") {
    if (!organisation) return <ForbiddenPage />;
    if (
      (requireOrgType === OrgTypes.ASSET_OWNER &&
        organisation.type !== OrgTypes.ASSET_OWNER) ||
      (requireOrgType === OrgTypes.CONTRACTOR &&
        organisation.type !== OrgTypes.CONTRACTOR)
    ) {
      return <ForbiddenPage />;
    }
  }

  if (requireUserLevel && user?.userLevel !== requireUserLevel) {
    return <ForbiddenPage />;
  }

  // Assets follows the org the user is Working As. A contractor stays out even
  // when Working In is a linked asset owner. Staff keep the page: it renders
  // empty when that org has no assets.
  if (requireWorkingAsAssetOwner && !workingInIsAssetOwner && !user?.isStaff) {
    return <ForbiddenPage />;
  }

  if (requireStaff && !user?.isStaff) return <ForbiddenPage />;

  if (!requireStaff && !allowStaff && user?.isStaff) return <ForbiddenPage />;

  return children;
};

function NotFoundPage() {
  return (
    <Container size="sm" py="xl">
      <Center mih="60vh">
        <Stack align="center" gap="lg">
          <Box>
            <IconAlertTriangle size={80} color="var(--mantine-color-gray-5)" />
          </Box>
          <Stack align="center" gap="md">
            <Title order={1} c="gray">
              404 - Page Not Found
            </Title>
            <Text size="lg" c="gray.6" ta="center">
              The page you are looking for does not exist.
            </Text>
            <Text size="sm" c="gray.6" ta="center">
              If you believe this is an error, please contact your system
              administrator.
            </Text>
            <Button
              leftSection={<IconHome size={16} />}
              onClick={() => (window.location.href = "/")}
              mt="md"
            >
              Go Home
            </Button>
          </Stack>
        </Stack>
      </Center>
    </Container>
  );
}

function ForbiddenPage() {
  return (
    <Container size="sm" py="xl">
      <Center mih="60vh">
        <Stack align="center" gap="lg">
          <Box>
            <IconLock size={80} color="var(--mantine-color-gray-5)" />
          </Box>
          <Stack align="center" gap="md">
            <Title order={1} c="gray">
              403 - Access Forbidden
            </Title>
            <Text size="lg" c="gray.6" ta="center">
              You do not have permission to access this page.
            </Text>
            <Text size="sm" c="gray.6" ta="center">
              If you believe this is an error, please contact your system
              administrator.
            </Text>
            <Button
              leftSection={<IconHome size={16} />}
              onClick={() => (window.location.href = "/")}
              mt="md"
            >
              Go Home
            </Button>
          </Stack>
        </Stack>
      </Center>
    </Container>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingLayout />}>
      <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password/request"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password/change"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/activate/:uid/:token"
          element={
            <PublicRoute>
              <ActivationPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowStaff={true}>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute allowUploadOnly={true} allowStaff={true}>
              <AccountSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inspections"
          element={
            <ProtectedRoute allowStaff={true}>
              <InspectionsProvider>
                <FiltersProvider>
                  <InspectionsPage />
                </FiltersProvider>
              </InspectionsProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inspection/:inspectionUuid"
          element={
            <ProtectedRoute allowStaff={true}>
              <InspectionProvider>
                <InspectionPage />
              </InspectionProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assets"
          element={
            <ProtectedRoute allowStaff requireWorkingAsAssetOwner>
              <AssetsProvider>
                <AssetsPage />
              </AssetsProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/asset/:assetUuid"
          element={
            <ProtectedRoute allowStaff requireWorkingAsAssetOwner>
              <AssetPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/uploads"
          element={
            <ProtectedRoute allowUploadOnly={true} allowStaff={true}>
              <UploadsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/organisations"
          element={
            <ProtectedRoute requireStaff={true}>
              <OrganisationsProvider>
                <OrganisationsPage />
              </OrganisationsProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organisation/:id"
          element={
            <ProtectedRoute requireStaff={true}>
              <OrganisationProvider>
                <OrganisationPage />
              </OrganisationProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute requireStaff={true}>
              <UsersProvider>
                <UserProvider>
                  <UsersPage />
                </UserProvider>
              </UsersProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/:id"
          element={
            <ProtectedRoute requireStaff={true}>
              <UsersProvider>
                <UserProvider>
                  <UsersPage />
                </UserProvider>
              </UsersProvider>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  const theme = {
    fontFamilyMonospace: "Monaco, Courier, monospace",
    focusRing: "auto",
    primaryColor: "teal",
    primaryShade: 7,
  };

  const AppWithErrorBoundary = () => (
    <MantineProvider theme={theme}>
      {hasRollbarToken ? (
        <ErrorBoundary fallbackUI={ErrorFallback}>
          <Notifications />
          <Router>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </Router>
        </ErrorBoundary>
      ) : (
        <ReactErrorBoundary
          FallbackComponent={ErrorFallback}
          onError={(error, errorInfo) => {
            console.error("ErrorBoundary caught an error:", error, errorInfo);
          }}
        >
          <Notifications />
          <Router>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </Router>
        </ReactErrorBoundary>
      )}
    </MantineProvider>
  );

  return hasRollbarToken ? (
    <RollbarProvider config={rollbarConfig}>
      <AppWithErrorBoundary />
    </RollbarProvider>
  ) : (
    <AppWithErrorBoundary />
  );
}

function AppContent() {
  const { isAuthenticated, authInitializing, user, appInitializing } =
    useContext(AuthContext);

  if (authInitializing) return null;

  if (appInitializing) return <LoadingLayout />;

  if (isAuthenticated) {
    return (
      <FoldersProvider>
        <UploadsProvider>
          <UploadProvider>
            {user?.userLevel !== UserLevels.UPLOAD_ONLY ? (
              <ExportsProvider>
                <Layout>
                  <ProductFruitsWrapper />
                  <NetworkStatusNotification />
                  <AppRoutes />
                </Layout>
              </ExportsProvider>
            ) : (
              <Layout>
                <ProductFruitsWrapper />
                <NetworkStatusNotification />
                <AppRoutes />
              </Layout>
            )}
          </UploadProvider>
        </UploadsProvider>
      </FoldersProvider>
    );
  } else {
    return (
      <Layout>
        <AppRoutes />
      </Layout>
    );
  }
}

export default App;
